import { useEffect, useState } from "react";
import { Award, Trophy, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { StarRating } from "@/components/StarRating";
import { api } from "@/lib/api";

const CRITERIA_LABELS = {
  pontualidade: "Pontualidade",
  gramatica: "Gramática",
  legistica: "Legística",
  dominio_tema: "Domínio do tema",
  qualidade_consolidacao: "Qualidade da consolidação",
};

function Podium({ ranking }) {
  if (ranking.length === 0) {
    return <div className="text-sm text-slate-500 py-6 text-center">Sem avaliações ainda.</div>;
  }
  return (
    <div className="space-y-3">
      {ranking.map((r, i) => {
        const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
        return (
          <div key={r.consultant_id} className="p-4 border border-slate-200 rounded-lg">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-midnight text-white flex items-center justify-center font-semibold">
                  {medal || (i + 1)}
                </div>
                <div>
                  <div className="font-medium text-slate-900">{r.consultant_name}</div>
                  <div className="text-xs text-slate-500">{r.total_ratings} avaliações registradas</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StarRating value={Math.round(r.avg)} readOnly size={18} testIdPrefix={`rank-stars-${i}`} />
                <div className="text-lg font-semibold text-gold-deep">{r.avg.toFixed(2)}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              {Object.entries(CRITERIA_LABELS).map(([k, label]) => (
                <div key={k} className="flex flex-col">
                  <span className="text-slate-500">{label}</span>
                  <span className="font-medium">{(r.breakdown[k] || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Ranking() {
  const [allRanking, setAllRanking] = useState(null);
  const [monthRanking, setMonthRanking] = useState(null);

  useEffect(() => {
    api.get("/ratings/ranking", { params: { period: "all" } }).then(({ data }) => setAllRanking(data.ranking));
    api.get("/ratings/ranking", { params: { period: "month" } }).then(({ data }) => setMonthRanking(data.ranking));
  }, []);

  if (!allRanking || !monthRanking) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Trophy className="w-6 h-6 text-gold-deep" /> Ranking de Consultores
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Avaliação cumulada entre Controle Jurídico e Diretora, em 5 critérios (1-5 estrelas).
        </p>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">
            <Award className="w-4 h-4 mr-2" /> Período total
          </TabsTrigger>
          <TabsTrigger value="month">
            <Star className="w-4 h-4 mr-2" /> Mês atual
          </TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <Card className="card-soft"><CardContent className="pt-6"><Podium ranking={allRanking} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="month">
          <Card className="card-soft"><CardContent className="pt-6"><Podium ranking={monthRanking} /></CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
